import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useAuth } from '@/stores/auth'
import { parishesApi } from '@/api/endpoints/parishes'
import { classesApi, type ClassResponse } from '@/api/endpoints/classes'
import { useUI } from '@/stores/ui'

interface Props {
  open: boolean
  onClose: () => void
  parishName?: string
  registrationUrl?: string
}

export default function RegistrationPdfModal({ open, onClose, parishName, registrationUrl }: Props) {
  const { user } = useAuth()
  const addToast = useUI((state) => state.addToast)
  const [classesHaveStarted, setClassesHaveStarted] = useState(false)
  const [firstDay, setFirstDay] = useState('')
  const [useAutoEndDate, setUseAutoEndDate] = useState(true)
  const [lastMonth, setLastMonth] = useState('')
  const [manualEntry, setManualEntry] = useState(false)
  const [weekdayDesc, setWeekdayDesc] = useState('')
  const [timeDesc, setTimeDesc] = useState('')
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([])
  const [classes, setClasses] = useState<ClassResponse[]>([])
  const [loadingClasses, setLoadingClasses] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Load classes when modal opens
  useEffect(() => {
    if (!open || !user?.parish_id) return
    
    let mounted = true
    const loadClasses = async () => {
      setLoadingClasses(true)
      try {
        const classList = await classesApi.list()
        if (mounted) setClasses(classList)
      } catch (error) {
        console.error('Failed to load classes:', error)
      } finally {
        if (mounted) setLoadingClasses(false)
      }
    }
    loadClasses()
    return () => { mounted = false }
  }, [open, user?.parish_id])

  useEffect(() => {
    if (!open) {
      setClassesHaveStarted(false)
      setFirstDay('')
      setUseAutoEndDate(true)
      setLastMonth('')
      setManualEntry(false)
      setWeekdayDesc('')
      setTimeDesc('')
      setSelectedClassIds([])
    }
  }, [open])

  const [autoFillError, setAutoFillError] = useState<string | null>(null)

  // Auto-fill details from classes
  const handleAutoFill = () => {
    if (classes.length === 0) {
      setAutoFillError('No classes created')
      return
    }
    setAutoFillError(null)
    
    // Find earliest start date that's after today
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const futureStartDates = classes
      .map(c => c.start_date ? new Date(c.start_date) : null)
      .filter(d => d && d >= today)
      .sort((a, b) => (a?.getTime() || 0) - (b?.getTime() || 0))
    
    if (futureStartDates.length > 0 && futureStartDates[0]) {
      setFirstDay(futureStartDates[0].toISOString().split('T')[0])
      setClassesHaveStarted(false)
    } else {
      // No future dates, check if classes have started
      const allStartDates = classes
        .map(c => c.start_date ? new Date(c.start_date) : null)
        .filter(d => d)
        .sort((a, b) => (a?.getTime() || 0) - (b?.getTime() || 0))
      
      if (allStartDates.length > 0 && allStartDates[0] && allStartDates[0] < today) {
        setClassesHaveStarted(true)
      }
    }
    
    // Find latest end date (approximate from start dates + 9 months)
    const endDates = classes
      .map(c => {
        if (!c.start_date) return null
        const start = new Date(c.start_date)
        const end = new Date(start)
        end.setMonth(end.getMonth() + 9) // Approximate school year
        return end
      })
      .filter(d => d)
      .sort((a, b) => (b?.getTime() || 0) - (a?.getTime() || 0))
    
    if (endDates.length > 0 && endDates[0]) {
      const endDate = endDates[0]
      setLastMonth(`${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}`)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 hover-raise"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Print Registration PDF for {parishName || 'Parish'} Classes</h2>
              <button className="btn-secondary text-sm focus-ring" onClick={onClose}>Close</button>
            </div>
            <div className="text-sm text-gray-600 mb-3">
              {parishName ? <span>Parish: <strong>{parishName}</strong></span> : <span>Parish details will be included.</span>}
            </div>
            <div className="mb-3">
              <div>
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  onClick={handleAutoFill}
                >
                  Auto-fill details
                </button>
                {autoFillError && (
                  <p className="text-xs text-red-600 mt-1">{autoFillError}</p>
                )}
                {!autoFillError && (
                  <p className="text-xs text-gray-500 mt-1">Note: You can still manually edit after clicking this button</p>
                )}
              </div>
            </div>
            <form
              className="space-y-3"
              onSubmit={async (e) => {
                e.preventDefault()
                if (!user?.parish_id || (!classesHaveStarted && !firstDay)) {
                  addToast({ message: 'Please fill in required fields', type: 'error' })
                  return
                }
                setSubmitting(true)
                try {
                  const blob = await parishesApi.generateRegistrationPDF(user.parish_id, {
                    first_day: classesHaveStarted ? undefined : firstDay,
                    last_month: !useAutoEndDate && lastMonth ? lastMonth : undefined,
                    use_school_year: useAutoEndDate,
                    class_days: manualEntry ? (weekdayDesc || undefined) : undefined,
                    class_time: manualEntry ? (timeDesc || undefined) : undefined,
                    class_ids: !manualEntry && selectedClassIds.length > 0 ? selectedClassIds : undefined,
                  })
                  
                  // Create download link
                  const url = window.URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `registration-flyer-${parishName?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'parish'}.pdf`
                  document.body.appendChild(a)
                  a.click()
                  document.body.removeChild(a)
                  window.URL.revokeObjectURL(url)
                  
                  addToast({ message: 'PDF downloaded successfully', type: 'success' })
                  onClose()
                } catch (error: any) {
                  console.error('Failed to generate PDF:', error)
                  addToast({
                    message: error.response?.data?.detail || 'Failed to generate PDF. Please try again.',
                    type: 'error',
                  })
                } finally {
                  setSubmitting(false)
                }
              }}
            >
              <div className="flex items-center gap-2">
                <input id="classesStarted" type="checkbox" className="focus-ring" checked={classesHaveStarted} onChange={(e) => setClassesHaveStarted(e.target.checked)} />
                <label htmlFor="classesStarted" className="text-sm">Classes have already started</label>
              </div>
              {!classesHaveStarted && (
                <div>
                  <label className="block text-sm font-medium mb-1">First day of class(es)</label>
                  <input type="date" className="input-field w-full focus-ring" value={firstDay} onChange={(e) => setFirstDay(e.target.value)} required={!classesHaveStarted} />
                </div>
              )}
              <div className="flex items-center gap-2">
                <input id="useAutoEndDate" type="checkbox" className="focus-ring" checked={useAutoEndDate} onChange={(e) => setUseAutoEndDate(e.target.checked)} />
                <label htmlFor="useAutoEndDate" className="text-sm">Automatically calculate end date (uncheck to set manually)</label>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Last month of class (if manually setting end date)</label>
                <input type="month" className="input-field w-full focus-ring" value={lastMonth} onChange={(e) => setLastMonth(e.target.value)} disabled={useAutoEndDate} />
              </div>
              <div className="flex items-center gap-2">
                <input id="manualEntry" type="checkbox" className="focus-ring" checked={manualEntry} onChange={(e) => setManualEntry(e.target.checked)} />
                <label htmlFor="manualEntry" className="text-sm">Manually enter class days and times</label>
              </div>
              {manualEntry ? (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Class day(s) of the week</label>
                    <input className="input-field w-full focus-ring" placeholder="Every Monday and Friday" value={weekdayDesc} onChange={(e) => setWeekdayDesc(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Time of classes</label>
                    <input className="input-field w-full focus-ring" placeholder="6:30 PM" value={timeDesc} onChange={(e) => setTimeDesc(e.target.value)} />
                  </div>
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Classes to include in PDF <span className="text-gray-500 font-normal">(optional)</span>
                  </label>
                  {loadingClasses ? (
                    <p className="text-sm text-gray-500">Loading classes...</p>
                  ) : classes.length === 0 ? (
                    <p className="text-sm text-gray-500">No classes available</p>
                  ) : (
                    <div className="space-y-2 border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                      {classes.map((cls) => (
                        <div key={cls.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                          <input
                            type="checkbox"
                            id={`class-${cls.id}`}
                            className="focus-ring"
                            checked={selectedClassIds.includes(cls.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedClassIds([...selectedClassIds, cls.id])
                              } else {
                                setSelectedClassIds(selectedClassIds.filter(id => id !== cls.id))
                              }
                            }}
                          />
                          <label htmlFor={`class-${cls.id}`} className="flex-1 text-sm cursor-pointer">
                            <div className="font-medium">{cls.name}</div>
                            <div className="text-xs text-gray-600">
                              {cls.weekly_time_description || 'No time specified'}; Location: {cls.address || 'Not specified'}
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {registrationUrl && (
                <div className="text-xs text-gray-600">Registration URL: <span className="break-all">{registrationUrl}</span></div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn-secondary focus-ring" onClick={onClose} disabled={submitting}>Cancel</button>
                <button type="submit" className="btn-primary focus-ring" disabled={submitting} title="Generate a printable PDF flyer with your parish details, class schedule, and registration QR code">{submitting ? 'Preparing…' : 'Download PDF'}</button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}



